import { parseHtmlString, serializeHtmlToString } from '../html-parse-serialize';
import getEditableTextFields, { TextFieldsData } from './editableTextFields/getData';
import saveEditableTextFields from './editableTextFields/saveData';
import getEditableMedia, { IndesignMediaElementConfig } from './editableMedia/getData';
import { MediaElement } from '../editableMedia/typing';
import saveEditableMedia from './editableMedia/saveData';
import getEditableLinks, { LinkConfig } from '../editableLinks/getData';
import saveEditableLinks from '../editableLinks/saveData';
import addTriggersToButtons from './buttons';
import { getVideoCoverImage, replaceVideosWithCoverImages } from '../videos';
import constants from './constants';
import { EditorType } from '../../constants';
import {
	getResourceUrl,
	getNumberFromString,
	isEditableButton,
} from '../helpers';
import { Api } from '../../../../typings/api';

interface PageSize {
	width: number;
	height: number;
}

interface PageData extends PageSize {
	html: string;
	editableMedia?: IndesignMediaElementConfig[];
	editableTextFields?: TextFieldsData;
	editableLinks?: LinkConfig[];
	isError: boolean;
}

const getPageSize = (htmlDocument: HTMLDocument): PageSize => {
	const wrapperDiv = htmlDocument.querySelector('div[style]') as HTMLElement;
	const { style: { width, height } } = wrapperDiv;
	return {
		width: getNumberFromString(width as string),
		height: getNumberFromString(height as string),
	};
};

const updateUrlsInPageHtml = (htmlDocument: HTMLDocument, config: Api.Config, resourceId: number): HTMLDocument => {
	const elementsWithSrc = [...htmlDocument.querySelectorAll('[src]')];

	elementsWithSrc.forEach((element: MediaElement) => {
		const url = getResourceUrl(element.src, config, resourceId);
		element.setAttribute('src', url);
	});

	return htmlDocument;
};

const getAllEditableElementsSelector = (): string => {
	const editableElementsSelectors = [
		`[class*=${constants.textFieldClassName}]:not([class*=${constants.lockedClassName}])`,
		`[class*=${constants.imageClassName}]:not([class*=${constants.lockedClassName}])`,
		`[class*=${constants.videoClassName}]:not([class*=${constants.lockedClassName}])`,
	];

	return editableElementsSelectors.join(',');
};

const getEditableElements = (htmlDocument: HTMLDocument): HTMLElement[] => ([
	...htmlDocument.querySelectorAll(getAllEditableElementsSelector()),
] as HTMLElement[]);

const removeEditableElements = (htmlDocument: HTMLDocument): HTMLDocument => {
	const elements = getEditableElements(htmlDocument);

	elements.forEach((element) => {
		const isVideo = element.classList.contains(constants.videoClassName);
		const elementToHide = isVideo
			? element.closest(`[id*=${constants.videoWrapperId}], [class*=${constants.videoWrapperId}]`) || element
			: element.querySelector(`#${element.id} > *`) as HTMLElement;
		elementToHide.parentNode!.removeChild(elementToHide);
	});

	return htmlDocument;
};

const addStylesToLockedElements = (htmlDocument: HTMLDocument): HTMLDocument => {
	const elements = [
		...htmlDocument.querySelectorAll(`[class*=${constants.lockedClassName}]`),
	] as HTMLElement[];

	elements.forEach((element) => {
		const iconWrapper = htmlDocument.createElement('div');
		const iconWrapperStyle = `
			position:absolute;
			bottom:2px;
			right:2px;
			display:flex;
			align-items:center;
			justify-content:center;
			width:22px;
			height:22px;
			border:1px solid #a4a4a4;
			border-radius:50%;
			background:#fff;
		`;
		iconWrapper.setAttribute('style', iconWrapperStyle);

		const icon = htmlDocument.createElement('svg');
		const iconStyle = `
			width:18px;
			height:18px;
			fill:#a4a4a4
		`;
		icon.setAttribute('style', iconStyle);

		const useElement = htmlDocument.createElement('use');
		useElement.setAttribute('xlink:href', '#svgicon-iconset_uprez_lock');

		icon.appendChild(useElement);
		iconWrapper.appendChild(icon);

		const isVideo = element.className.includes(constants.videoClassName);

		if (isVideo) {
			const { parentNode } = element;

			if (parentNode) {
				const videoWrapper = element.closest(`[id*=${constants.videoWrapperId}], [class*=${constants.videoWrapperId}]`);
				const { image, defaultImage } = getVideoCoverImage(element, htmlDocument);

				if (videoWrapper) {
					videoWrapper.classList.add(constants.lockedClassName);
				}

				parentNode.removeChild(element);
				parentNode.appendChild(image);
				parentNode.appendChild(defaultImage);
				parentNode.appendChild(iconWrapper);
			}
		} else {
			element.appendChild(iconWrapper);
		}
	});

	return htmlDocument;
};

const parseLinks = (htmlDocument: HTMLDocument): HTMLDocument => {
	const linkElements = [...htmlDocument.querySelectorAll('a')];

	linkElements.forEach((linkElement) => {
		linkElement.removeAttribute('href');

		if (isEditableButton(linkElement)) {
			const parentLinkElement = linkElement.closest(`[id*=${constants.parentContainerId}]`) as HTMLElement;

			if (parentLinkElement) {
				parentLinkElement.classList.add(constants.editableButtonClassName);

				const lockedText = parentLinkElement.querySelector(`[class*=${constants.textFieldClassName}][class*=${constants.lockedClassName}]`);

				if (lockedText) {
					lockedText.className = lockedText.className.replace(constants.lockedClassName, '');
					parentLinkElement.classList.add(constants.lockedClassName);
				}
			}
		}
	});

	return htmlDocument;
};

export const getModifiedPageData = (
	page: number,
	htmlString: string,
	css: string,
	config: Api.Config,
	resourceId: number,
	isEditablePage: boolean,
	isModifiedFromBackoffice: boolean,
	pageHeightFromConfig?: number,
): PageData => {
	const pageData: PageData = {
		html: htmlString,
		width: 0,
		height: 0,
		isError: false,
	};

	// try catch block is used to avoid app breaking in case something went wrong in parsing of inDesign html document
	try {
		const defaultHtmlDocument = parseHtmlString(htmlString);
		let htmlDocument = parseHtmlString(htmlString);

		if (isEditablePage) {
			htmlDocument = addTriggersToButtons(htmlDocument);
			htmlDocument = removeEditableElements(htmlDocument);
			htmlDocument = parseLinks(htmlDocument);
		}

		// this method should be called after removeEditableElements to not update urls on editable images/videos as they will be removed anyway
		htmlDocument = updateUrlsInPageHtml(htmlDocument, config, resourceId);

		if (isEditablePage) {
			// this method should be called after updateUrlsInPageHtml as locked videos should be replaced with cover images with correct urls
			htmlDocument = addStylesToLockedElements(htmlDocument);
		}

		if (!isEditablePage) {
			htmlDocument = replaceVideosWithCoverImages(htmlDocument);
		}

		const { width, height } = getPageSize(htmlDocument);

		pageData.html = serializeHtmlToString(htmlDocument);
		pageData.width = width;
		pageData.height = pageHeightFromConfig || height;

		if (isEditablePage) {
			const editableTextFields = getEditableTextFields(page, defaultHtmlDocument, css, isModifiedFromBackoffice);
			const editableMedia = getEditableMedia(page, defaultHtmlDocument, css);
			const editableLinks = getEditableLinks(page, defaultHtmlDocument, EditorType.INDESIGN);
			pageData.editableTextFields = editableTextFields;
			pageData.editableMedia = editableMedia.items;
			pageData.editableLinks = editableLinks.items;
			pageData.isError = editableTextFields.isError || editableMedia.isError || editableLinks.isError;
		}
	} catch (error) {
		pageData.isError = true;
	}

	return pageData;
};

export const getPageHtmlToSave = (htmlString: string, { editableTextFields, editableMedia, editableLinks }): string => {
	const htmlDocument = parseHtmlString(htmlString);
	saveEditableTextFields(htmlDocument, editableTextFields);
	saveEditableMedia(htmlDocument, editableMedia);
	saveEditableLinks(htmlDocument, editableLinks, EditorType.INDESIGN);

	return serializeHtmlToString(htmlDocument);
};



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/containers/Editor/utils/indesignEditor/html.ts